import { Field } from '@web/components/atoms/Field'
import { Input, NumberInputField, NumberInputRoot } from '@web/components/atoms/Input'
import { Textarea } from '@web/components/atoms/Textarea'
import { CampaignAsset, ProfileData } from '@web/graphql-types'
import { useRef, useState } from 'react'
import FilePicker from '@web/components/molecules/FilePicker'
import { useMutation } from '@tanstack/react-query'
import { useHttpClient } from 'apps/fe-donation/libs/hooks/useHttpClient'
import { usePageStore } from 'apps/fe-donation/libs/hooks/usePageStore'
import { Box, Group } from '@web/components/atoms/Layout'
import { Image } from '@web/components/atoms/Image'
import { Checkbox } from '@web/components/atoms/Checkbox'

type UpdateFundraiserProfileFormProps = {
  profileData: ProfileData
}

const UpdateFundraiserProfileForm = ({ profileData }: UpdateFundraiserProfileFormProps) => {
  const uploadRef = useRef<HTMLInputElement>(null)
  const httpClient = useHttpClient({ isRelativeUrl: true })
  const [avatarUrl, setAvatarUrl] = useState(profileData.logoUrl)
  const [isGivingNotificationEnabled, setIsGivingNotificationEnabled] = useState(
    profileData?.isGivingNotificationEnabled ?? false,
  )

  const { isPeerToPeerGivingNotificationEnabled } = usePageStore.getState()

  const {
    mutateAsync: uploadAsset,
    isPending: isUploading,
    error,
  } = useMutation({
    mutationKey: ['uploadAsset'],
    mutationFn: ({ file }: { file: File }) => {
      const state = usePageStore.getState()
      const formData = new FormData()

      formData.append('file', new File([file], file.name, { type: file.type }))
      formData.append('name', file.name)
      formData.append('profileId', state.profileId!)
      formData.append('campaignId', state.campaignId)
      formData.append('charityId', state.charityId)

      return httpClient<CampaignAsset>(`/api/profile/assets`, {
        method: 'POST',
        body: formData,
      })
    },
    onSuccess: (data) => {
      setAvatarUrl(data.url)
    },
    onError: (e) => {
      uploadRef.current!.value = ''
    },
  })
  return (
    <>
      <input type="hidden" name="logoUrl" value={avatarUrl ?? ''} />
      <Field label="Name" required={true}>
        <Input name="name" placeholder="Enter name" defaultValue={profileData?.name} />
      </Field>
      <Field label="Goal ($)" required={true}>
        <NumberInputRoot name="goal" defaultValue={`${profileData?.goal ?? 0}`} min={1}>
          <NumberInputField placeholder="Enter goal" />
        </NumberInputRoot>
      </Field>
      <Group alignSelf="start">
        {avatarUrl && (
          <Box w="100%" h="100%" bgColor="ge.white" borderRadius="6px" overflow="hidden">
            <Image src={avatarUrl} alt="avatar" width="50px" height="50px" />
          </Box>
        )}
        <Field label="Avatar" invalid={!!error} errorText="Something went wrong. Please try again.">
          <FilePicker
            id="upload-avatar"
            acceptedFileTypes="image/*"
            inputRef={uploadRef}
            label="Upload"
            isLoading={isUploading}
            onChange={(e) => {
              if (!e.target.files) {
                return
              }

              uploadAsset({ file: e.target.files[0] })
            }}
          />
        </Field>
      </Group>
      <Field label="Description">
        <Textarea
          name="description"
          placeholder="Enter description"
          defaultValue={profileData?.description ?? undefined}
        />
      </Field>
      {isPeerToPeerGivingNotificationEnabled && (
        <Field>
          <input type="hidden" name="isGivingNotificationEnabled" value={`${isGivingNotificationEnabled}`} />
          <Checkbox
            defaultChecked={profileData?.isGivingNotificationEnabled ?? false}
            onCheckedChange={(checked) => setIsGivingNotificationEnabled(checked?.checked === true)}
          >
            I wish to receive an email notification whenever a donation is made
          </Checkbox>
        </Field>
      )}
    </>
  )
}

export default UpdateFundraiserProfileForm
